import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import DescriptionIcon from '@mui/icons-material/Description';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Swal from 'sweetalert2';
import apiClient from "@/api/client";

const SuttaView = () => {
    const [suttas, setSuttas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isTableLoading, setIsTableLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [editingSutta, setEditingSutta] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        sutta_title: '',
        event_title: '',
        retreat_no: '',
        event_location: '',
        event_start_date: '',
        event_end_date: '',
        links: []
    });

    // Link form state
    const [linkData, setLinkData] = useState({
        title: '',
        url: '',
        type: 'document'
    });

    const linkTypes = [
        { value: 'audio', label: 'Audio Recording', icon: AudiotrackIcon },
        { value: 'video', label: 'Video Recording', icon: VideoLibraryIcon },
        { value: 'document', label: 'Document/PDF', icon: DescriptionIcon },
        { value: 'link', label: 'General Link', icon: LinkIcon }
    ];

    useEffect(() => {
        fetchSuttas();
    }, []);

    const fetchSuttas = async () => {
    try {
        setIsTableLoading(true);
        const response = await apiClient.get('/dhamma-talks');
        const result = response.data;
        setSuttas(result.data || result);
    } catch (error) {
        console.error('Error fetching suttas:', error);
    } finally {
        setIsTableLoading(false);
    }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        setErrors({ ...errors, [name]: "" });
    };

    const handleLinkChange = (event) => {
        const { name, value } = event.target;
        setLinkData({
            ...linkData,
            [name]: value,
        });
    };

    const addLink = () => {
        if (!linkData.title.trim() || !linkData.url.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Information',
                text: 'Please provide both title and URL for the link.',
                confirmButtonText: 'OK',
            });
            return;
        }

        // Basic URL validation
        try {
            new URL(linkData.url);
        } catch (e) {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid URL',
                text: 'Please enter a valid URL.',
                confirmButtonText: 'OK',
            });
            return;
        }

        setFormData({
            ...formData,
            links: [...formData.links, { ...linkData }]
        });

        setLinkData({
            title: '',
            url: '',
            type: 'document'
        });
    };

    const removeLink = (index) => {
        const updatedLinks = formData.links.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            links: updatedLinks
        });
    };

    const validateForm = () => {
        let tempErrors = {};
        let isValid = true;

        if (!formData.sutta_title.trim()) {
            tempErrors.sutta_title = "Sutta title is required";
            isValid = false;
        }

        if (!formData.event_title.trim()) {
            tempErrors.event_title = "Event title is required";
            isValid = false;
        }

        if (!formData.retreat_no.trim()) {
            tempErrors.retreat_no = "Retreat number is required";
            isValid = false;
        }

        if (!formData.event_location.trim()) {
            tempErrors.event_location = "Event location is required";
            isValid = false;
        }

        if (!formData.event_start_date) {
            tempErrors.event_start_date = "Start date is required";
            isValid = false;
        }

        if (!formData.event_end_date) {
            tempErrors.event_end_date = "End date is required";
            isValid = false;
        } else if (new Date(formData.event_start_date) > new Date(formData.event_end_date)) {
            tempErrors.event_end_date = "End date must be after start date";
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    const resetForm = () => {
        setFormData({
            sutta_title: '',
            event_title: '',
            retreat_no: '',
            event_location: '',
            event_start_date: '',
            event_end_date: '',
            links: []
        });
        setLinkData({
            title: '',
            url: '',
            type: 'document'
        });
        setErrors({});
        setEditingSutta(null);
    };

    const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    setIsLoading(true);
    try {
        const url = editingSutta ? `/dhamma-talks/${editingSutta.id}` : '/dhamma-talks';
        const method = editingSutta ? 'PUT' : 'POST';

        let response;
        if (method === 'PUT') {
        response = await apiClient.put(url, formData);
        } else {
        response = await apiClient.post(url, formData);
        }
        const result = response.data;

        console.log('Success:', result);

        // Close dialog first to avoid z-index conflicts
        setOpenDialog(false);

        // Small delay to ensure dialog closes before showing alert
        setTimeout(() => {
        Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: `Sutta record ${editingSutta ? 'updated' : 'created'} successfully!`,
            confirmButtonText: 'OK',
            zIndex: 10000,
        }).then(() => {
            resetForm();
            fetchSuttas();
        });
        }, 100);
    } catch (error) {
        console.error('Error:', error);
        // Extract error message from Axios error
        let errorMessage = error.message;
        if (error.response && error.response.data) {
        if (error.response.data.errors) {
            errorMessage = Object.values(error.response.data.errors).flat().join('\n');
        } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
        }
        }
        // Close dialog first if there's an error too
        setOpenDialog(false);
        setTimeout(() => {
        Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: errorMessage,
            confirmButtonText: 'OK',
            zIndex: 10000,
        }).then(() => {
            // Reopen dialog on error so user can try again
            setOpenDialog(true);
        });
        }, 100);
    } finally {
        setIsLoading(false);
    }
    };

    const handleEdit = (sutta) => {
        setEditingSutta(sutta);
        setFormData({
            sutta_title: sutta.sutta_title || '',
            event_title: sutta.event_title || '',
            retreat_no: sutta.retreat_no || '',
            event_location: sutta.event_location || '',
            event_start_date: sutta.event_start_date ? sutta.event_start_date.split('T')[0] : '',
            event_end_date: sutta.event_end_date ? sutta.event_end_date.split('T')[0] : '',
            links: sutta.links || []
        });
        setOpenDialog(true);
    };

    const handleDelete = async (suttaId) => {
    const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
        try {
        await apiClient.delete(`/dhamma-talks/${suttaId}`);
        Swal.fire('Deleted!', 'Sutta record has been deleted.', 'success');
        fetchSuttas();
        } catch (error) {
        console.error('Error:', error);
        Swal.fire('Error!', 'Failed to delete sutta record.', 'error');
        }
    }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getTypeIcon = (type) => {
        const linkType = linkTypes.find(lt => lt.value === type);
        const IconComponent = linkType ? linkType.icon : LinkIcon;
        return <IconComponent fontSize="small" />;
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'audio': return 'primary';
            case 'video': return 'secondary';
            case 'document': return 'default';
            default: return 'info';
        }
    };

    return (
        <Box sx={{ minWidth: 275 }}>
            {/* Create Sutta Button */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5" component="div">
                            Sutta Management
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => {
                                resetForm();
                                setOpenDialog(true);
                            }}
                        >
                            Create New Sutta Record
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Suttas Table */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
                        All Sutta Records
                    </Typography>
                    <hr />
                    {isTableLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Sutta Title</TableCell>
                                        <TableCell>Event</TableCell>
                                        <TableCell>Retreat No.</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell>Duration</TableCell>
                                        <TableCell>Resources</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {suttas.length > 0 ? (
                                        suttas.map((sutta) => (
                                            <TableRow key={sutta.id}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {sutta.sutta_title}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {sutta.event_title}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={sutta.retreat_no} size="small" variant="outlined" />
                                                </TableCell>
                                                <TableCell>{sutta.event_location}</TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" display="block">
                                                        {formatDate(sutta.event_start_date)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        to {formatDate(sutta.event_end_date)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {sutta.links && sutta.links.map((link, index) => (
                                                            <Chip
                                                                key={index}
                                                                icon={getTypeIcon(link.type)}
                                                                label={link.title}
                                                                size="small"
                                                                color={getTypeColor(link.type)}
                                                                variant="outlined"
                                                                onClick={() => window.open(link.url, '_blank')}
                                                                sx={{ cursor: 'pointer' }}
                                                            />
                                                        ))}
                                                        {(!sutta.links || sutta.links.length === 0) && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                No resources
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        onClick={() => handleEdit(sutta)}
                                                        color="primary"
                                                        size="small"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => handleDelete(sutta.id)}
                                                        color="error"
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                No sutta records found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Sutta Dialog */}
            <Dialog 
                open={openDialog} 
                onClose={() => setOpenDialog(false)} 
                maxWidth="md" 
                fullWidth
                style={{ zIndex: 1300 }} // Ensure dialog has proper z-index
            >
                <DialogTitle>
                    {editingSutta ? 'Edit Sutta Record' : 'Create New Sutta Record'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="sutta_title"
                                label="Sutta Title"
                                placeholder="Enter sutta title"
                                variant="outlined"
                                value={formData.sutta_title}
                                onChange={handleChange}
                                error={!!errors.sutta_title}
                                helperText={errors.sutta_title}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="event_title"
                                label="Event Title"
                                placeholder="Enter event title"
                                variant="outlined"
                                value={formData.event_title}
                                onChange={handleChange}
                                error={!!errors.event_title}
                                helperText={errors.event_title}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="retreat_no"
                                label="Retreat Number"
                                placeholder="e.g., RET2024-001"
                                variant="outlined"
                                value={formData.retreat_no}
                                onChange={handleChange}
                                error={!!errors.retreat_no}
                                helperText={errors.retreat_no}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="event_location"
                                label="Event Location"
                                placeholder="Enter event location"
                                variant="outlined"
                                value={formData.event_location}
                                onChange={handleChange}
                                error={!!errors.event_location}
                                helperText={errors.event_location}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="event_start_date"
                                label="Event Start Date"
                                type="date"
                                variant="outlined"
                                value={formData.event_start_date}
                                onChange={handleChange}
                                error={!!errors.event_start_date}
                                helperText={errors.event_start_date}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="event_end_date"
                                label="Event End Date"
                                type="date"
                                variant="outlined"
                                value={formData.event_end_date}
                                onChange={handleChange}
                                error={!!errors.event_end_date}
                                helperText={errors.event_end_date}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>

                        {/* Links Section */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }}>
                                <Typography variant="h6" color="text.secondary">
                                    Resource Links
                                </Typography>
                            </Divider>
                        </Grid>

                        {/* Add Link Form */}
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                name="title"
                                label="Link Title"
                                placeholder="e.g., Audio Recording"
                                variant="outlined"
                                value={linkData.title}
                                onChange={handleLinkChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                name="url"
                                label="URL"
                                placeholder="https://example.com/resource"
                                variant="outlined"
                                value={linkData.url}
                                onChange={handleLinkChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    name="type"
                                    value={linkData.type}
                                    onChange={handleLinkChange}
                                    label="Type"
                                >
                                    {linkTypes.map((type) => (
                                        <MenuItem key={type.value} value={type.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <type.icon fontSize="small" />
                                                {type.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={1}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={addLink}
                                sx={{ height: '56px' }}
                            >
                                <AddIcon />
                            </Button>
                        </Grid>

                        {/* Existing Links List */}
                        {formData.links.length > 0 && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Added Links ({formData.links.length})
                                </Typography>
                                <List dense>
                                    {formData.links.map((link, index) => (
                                        <ListItem key={index} divider>
                                            <ListItemIcon>
                                                {getTypeIcon(link.type)}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={link.title}
                                                secondary={link.url}
                                            />
                                            <ListItemSecondaryAction>
                                                <Chip
                                                    label={linkTypes.find(t => t.value === link.type)?.label || link.type}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => removeLink(index)}
                                                    color="error"
                                                    size="small"
                                                    sx={{ ml: 1 }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : null}
                    >
                        {isLoading ? (editingSutta ? 'Updating...' : 'Creating...') : (editingSutta ? 'Update Record' : 'Create Record')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SuttaView;